
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivation } from '@/contexts/ActivationContext';
import { useAuth } from '@/contexts/AuthContext';
import { getEndpointUrl } from '@/lib/api-config';
import { formatRut } from '@/lib/rut';
import BANKS from '@/data/banks.json';

const ACCOUNT_TYPES = ['Cuenta Corriente', 'Cuenta Vista / RUT', 'Chequera Electrónica'];

interface BankAccount {
  bank: string;
  accountType: string;
  accountNumber: string;
  holderRut: string;
  holderEmail: string;
}

type FormMode = 'loading' | 'view' | 'edit' | 'new';

export default function CuentaBancariaPanel() {
  const navigate = useNavigate();
  const { isStepComplete, refreshStatus } = useActivation();
  const { user, refreshUser } = useAuth();
  const isCompleted = isStepComplete('cuentaBancaria');
  // El RUT del titular debe coincidir con el RUT de la empresa (validado contra el SII).
  // Nunca es editable: se pre-llena desde el contexto de auth.
  const companyRut = user?.companyTaxId ? formatRut(user.companyTaxId) : '';

  const [mode, setMode] = useState<FormMode>('loading');
  const [bank, setBank] = useState('');
  const [accountType, setAccountType] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [holderRut, setHolderRut] = useState(companyRut);
  const [holderEmail, setHolderEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!isCompleted) {
      // Asegurar que tenemos el companyTaxId más fresco antes de mostrar el form.
      // El RUT se sincroniza desde el SII; sin este refresh el campo puede quedar
      // vacío o con un valor previo y el backend rechazará el guardado con
      // HOLDER_RUT_MISMATCH.
      refreshUser().catch(() => {
        // Si falla, seguimos con el valor cacheado del contexto.
      });
      setHolderRut(companyRut);
      setMode('new');
      return;
    }

    const loadAccount = async () => {
      try {
        const url = getEndpointUrl('bank', 'get');
        const { httpClient } = await import('@/lib/http-client');
        const data = await httpClient.get<BankAccount>(url);
        setBank(data.bank);
        setAccountType(data.accountType);
        setAccountNumber(data.accountNumber);
        setHolderRut(data.holderRut || companyRut);
        setHolderEmail(data.holderEmail);
        setMode('view');
      } catch {
        setLoadError('No se pudieron cargar los datos bancarios. Intenta nuevamente.');
        setMode('view');
      }
    };

    loadAccount();
  }, [isCompleted, companyRut, refreshUser]);

  const handleEnableEdit = () => {
    setError('');
    setMode('edit');
  };

  const handleCancelEdit = () => {
    setError('');
    setMode('view');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!bank || !accountType || !accountNumber || !holderRut || !holderEmail) {
      setError('Por favor completa todos los campos antes de guardar.');
      return;
    }
    setSubmitting(true);
    try {
      const { httpClient } = await import('@/lib/http-client');
      const payload = { bank, accountType, accountNumber, holderRut, holderEmail };
      if (mode === 'edit') {
        const url = getEndpointUrl('bank', 'update');
        await httpClient.put(url, payload);
        await refreshStatus();
        setMode('view');
      } else {
        const url = getEndpointUrl('bank', 'save');
        await httpClient.post(url, payload);
        await refreshStatus();
        navigate('/configuraciones/resumen');
      }
    } catch (err: any) {
      // Si el backend rechazó por desincronización con el SII, intentamos refrescar
      // el RUT del titular y damos un mensaje accionable.
      if (err?.data?.code === 'HOLDER_RUT_MISMATCH') {
        try {
          await refreshUser();
        } catch {
          // ignorar
        }
        setError(err?.data?.message || 'El RUT del titular debe coincidir con el RUT de la empresa registrada en el SII. Intenta guardar nuevamente.');
      } else {
        const message = err?.data?.message || err?.message;
        setError(message || 'Error al guardar la cuenta bancaria. Intenta nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = mode === 'view';
  const isLoading = mode === 'loading';

  return (
    <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px]">
      <div className="p-6 md:p-8">
        <div className="mb-8 border-b border-slate-100 dark:border-slate-800 pb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              {isCompleted && !isLoading && (
                <span className="material-symbols-outlined text-emerald-500 text-2xl">verified</span>
              )}
              Cuenta Bancaria
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              {isReadOnly
                ? 'Tu cuenta bancaria está registrada. Puedes editarla si necesitas actualizarla.'
                : 'Configura la cuenta donde recibirás tus abonos y adelantos. Asegúrate de ingresar los datos correctamente.'}
            </p>
          </div>
        </div>

        {loadError && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">warning</span>
            {loadError}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
              <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
              <div className="col-span-2 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
              <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
              <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
            </div>
            <div className="h-11 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse mt-4 w-40 ml-auto" />
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-1 md:col-span-2">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">account_balance</span>
                      Datos de la Cuenta
                    </h3>
                  </div>

                  <div>
                    <label
                      htmlFor="bank_name"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      Banco
                    </label>
                    <select
                      id="bank_name"
                      name="bank_name"
                      value={bank}
                      onChange={(e) => setBank(e.target.value)}
                      disabled={isReadOnly}
                      className={`block w-full pl-3 pr-10 py-2 text-sm border rounded-md focus:outline-none focus:ring-primary focus:border-primary
                        ${isReadOnly
                          ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default'
                          : 'border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white'
                        }`}
                    >
                      <option value="">Selecciona tu banco</option>
                      {BANKS.map((b) => (
                        <option key={b.code} value={b.code}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="account_type"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      Tipo de Cuenta
                    </label>
                    <select
                      id="account_type"
                      name="account_type"
                      value={accountType}
                      onChange={(e) => setAccountType(e.target.value)}
                      disabled={isReadOnly}
                      className={`block w-full pl-3 pr-10 py-2 text-sm border rounded-md focus:outline-none focus:ring-primary focus:border-primary
                        ${isReadOnly
                          ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default'
                          : 'border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white'
                        }`}
                    >
                      <option value="">Selecciona el tipo</option>
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label
                      htmlFor="account_number"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      Número de Cuenta
                    </label>
                    <input
                      id="account_number"
                      name="account_number"
                      type="text"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="Ej: 123456789"
                      className={`block w-full pl-3 py-2 sm:text-sm border rounded-md focus:ring-primary focus:border-primary
                        ${isReadOnly
                          ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default select-none'
                          : 'border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white'
                        }`}
                    />
                    {!isReadOnly && (
                      <p className="mt-1 text-xs text-slate-500">Ingresa el número sin guiones ni espacios.</p>
                    )}
                  </div>

                  <div className="col-span-1 md:col-span-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">person</span>
                      Datos del Titular
                    </h3>
                  </div>

                  <div>
                    <label
                      htmlFor="holder_rut"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      RUT del Titular
                    </label>
                    <input
                      id="holder_rut"
                      name="holder_rut"
                      type="text"
                      value={holderRut}
                      readOnly
                      aria-readonly
                      placeholder="12.345.678-9"
                      className="block w-full pl-3 py-2 sm:text-sm border rounded-md border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-not-allowed select-none"
                    />
                    {!isReadOnly && (
                      <p className="mt-1 text-xs text-slate-500">
                        Coincide con el RUT de tu empresa registrado en el SII. Los adelantos se depositan a una cuenta a nombre de la empresa.
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="holder_email"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      Email de Confirmación
                    </label>
                    <input
                      id="holder_email"
                      name="holder_email"
                      type="email"
                      value={holderEmail}
                      onChange={(e) => setHolderEmail(e.target.value)}
                      readOnly={isReadOnly}
                      placeholder="contacto@empresa.com"
                      className={`block w-full pl-3 py-2 sm:text-sm border rounded-md focus:ring-primary focus:border-primary
                        ${isReadOnly
                          ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default select-none'
                          : 'border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white'
                        }`}
                    />
                  </div>

                  {!isReadOnly && (
                    <div className="col-span-1 md:col-span-2">
                      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-5 border border-blue-100 dark:border-blue-900/30 flex items-start gap-4">
                        <div className="bg-white dark:bg-blue-900/30 rounded-full p-2 flex-shrink-0 border border-blue-100 dark:border-blue-800">
                          <span className="material-symbols-outlined text-primary text-xl">verified_user</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                            Verificación de Cuenta
                          </h4>
                          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                            Para garantizar la seguridad de tus fondos y confirmar la titularidad de la cuenta
                            bancaria, FLUX podría realizar una{' '}
                            <strong>pequeña transferencia de verificación</strong> (monto menor a $100 CLP). Este
                            proceso es automático y no requiere acción de tu parte.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-span-1 md:col-span-2 flex justify-end gap-3 pt-2">
                    {isReadOnly ? (
                      <button
                        type="button"
                        onClick={handleEnableEdit}
                        className="inline-flex justify-center items-center px-6 py-3 border border-slate-300 dark:border-slate-600 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-all duration-200"
                      >
                        <span className="material-symbols-outlined text-base mr-2">edit</span>
                        Editar cuenta
                      </button>
                    ) : (
                      <>
                        {mode === 'edit' && (
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="inline-flex justify-center items-center px-5 py-3 text-sm font-medium rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          >
                            Cancelar
                          </button>
                        )}
                        <button
                          type="submit"
                          disabled={submitting}
                          className="inline-flex justify-center items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <>
                              <span className="material-symbols-outlined text-base animate-spin mr-2">
                                progress_activity
                              </span>
                              Guardando...
                            </>
                          ) : (
                            <>
                              <span className="material-symbols-outlined mr-2">save</span>
                              Guardar Cambios
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
